import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle,
  X,
  Send,
  Minimize2,
  Loader2,
  RotateCcw,
  AlertCircle,
  Sparkles,
  Compass,
} from 'lucide-react';
import { formatChatTime, renderChatMarkdownLite } from '../../utils/chatWidgetUtils';

/**
 * Shared floating chat UI for support (logged-in) and guest concierge.
 */
const ChatWidgetShell = ({
  variant = 'support',
  isOpen,
  isMinimized,
  chatEnabled,
  assistantLabel,
  subtitle,
  messages,
  inputValue,
  onInputChange,
  onSend,
  isSending,
  error,
  onToggle,
  onClose,
  onClear,
  quickPrompts = [],
  onQuickPrompt,
  openButtonLabel = 'Open chat',
  fabIcon: FabIcon = MessageCircle,
  fabClassName = 'bg-black',
}) => {
  const endRef = useRef(null);
  const isConcierge = variant === 'concierge';

  useEffect(() => {
    if (isOpen && !isMinimized) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, isMinimized, messages, isSending, endRef]);
  const headerIcon = isConcierge ? Compass : Sparkles;
  const HeaderIcon = headerIcon;

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 ${fabClassName} text-white rounded-full border-2 border-white shadow-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center ${
          isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-label={openButtonLabel}
      >
        <FabIcon className="w-6 h-6" />
        {chatEnabled && (
          <span
            className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
              isConcierge ? 'bg-violet-500' : 'bg-emerald-500'
            }`}
            title="AI online"
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100%-2rem)] sm:w-[400px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col transition-all duration-300 border ${
            isConcierge ? 'border-violet-200' : 'border-gray-200'
          } ${isMinimized ? 'h-16' : 'h-[min(640px,calc(100vh-5rem))]'}`}
          role="dialog"
          aria-label={assistantLabel}
        >
          <div
            className={`text-white px-4 py-3 rounded-t-2xl flex items-center justify-between shrink-0 ${
              isConcierge ? 'bg-gradient-to-r from-violet-900 to-black' : 'bg-black'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <HeaderIcon className={`w-5 h-5 shrink-0 ${isConcierge ? 'text-violet-200' : 'text-amber-300'}`} />
              <div className="min-w-0">
                <h3 className="font-medium truncate">{assistantLabel}</h3>
                <p className="text-[10px] text-gray-400 truncate">{subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!isMinimized && (
                <button
                  type="button"
                  onClick={onClear}
                  className="p-1.5 hover:bg-white/10 rounded transition-colors"
                  aria-label="Clear conversation"
                  title="Clear chat"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button type="button" onClick={onToggle} className="p-1.5 hover:bg-white/10 rounded transition-colors" aria-label="Minimize">
                <Minimize2 className="w-4 h-4" />
              </button>
              <button type="button" onClick={onClose} className="p-1.5 hover:bg-white/10 rounded transition-colors" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {error && (
                <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-start gap-2 text-xs text-red-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {quickPrompts.length > 0 && (
                <div className="px-3 pt-3 pb-1 flex flex-wrap gap-2 bg-gray-50 border-b border-gray-100">
                  {quickPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => onQuickPrompt?.(prompt)}
                      disabled={isSending || !chatEnabled}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-300 bg-white text-gray-800 hover:border-black hover:bg-gray-50 disabled:opacity-50"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 min-h-0">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className="max-w-[90%] space-y-2">
                      <div
                        className={`rounded-2xl px-4 py-2.5 ${
                          message.sender === 'user'
                            ? isConcierge
                              ? 'bg-violet-900 text-white rounded-br-md'
                              : 'bg-black text-white rounded-br-md'
                            : message.isError
                              ? 'bg-red-50 text-red-900 border border-red-200 rounded-bl-md'
                              : 'bg-white text-black border border-gray-200 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                          {renderChatMarkdownLite(message.text)}
                        </p>
                        <p
                          className={`text-[10px] mt-1.5 ${
                            message.sender === 'user' ? 'text-white/60' : 'text-gray-500'
                          }`}
                        >
                          {formatChatTime(message.timestamp)}
                        </p>
                      </div>

                      {message.actions?.length > 0 && (
                        <div className="flex flex-col gap-1.5 pl-0.5">
                          {message.actions.map((action) => (
                            <Link
                              key={`${action.path}-${action.label}`}
                              to={action.path}
                              onClick={onClose}
                              className={`inline-flex items-center justify-center text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                                action.type === 'book'
                                  ? isConcierge
                                    ? 'bg-violet-900 text-white border-violet-900 hover:bg-violet-800'
                                    : 'bg-black text-white border-black hover:bg-gray-800'
                                  : 'bg-white text-gray-900 border-gray-300 hover:border-black'
                              }`}
                            >
                              {action.label}
                            </Link>
                          ))}
                        </div>
                      )}

                      {message.suggestions?.length > 0 && (
                        <div className="space-y-1.5">
                          {message.suggestions.slice(0, 3).map((s) => (
                            <div
                              key={s.service_id}
                              className="text-xs bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm"
                            >
                              <p className="font-medium text-gray-900">{s.service_name}</p>
                              <p className="text-gray-600">{s.vendor_name}</p>
                              {s.price_from > 0 && (
                                <p className="text-gray-500 mt-0.5">From ${Number(s.price_from).toFixed(2)}</p>
                              )}
                              <div className="flex gap-2 mt-2">
                                <Link
                                  to={s.book_path}
                                  onClick={onClose}
                                  className={`flex-1 text-center py-1.5 rounded-md text-white text-[11px] font-medium ${
                                    isConcierge ? 'bg-violet-900' : 'bg-black'
                                  }`}
                                >
                                  Book now
                                </Link>
                                <Link
                                  to={s.profile_path}
                                  onClick={onClose}
                                  className="flex-1 text-center py-1.5 rounded-md border border-gray-300 text-[11px]"
                                >
                                  Profile
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {isSending && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm flex items-center gap-2 text-sm text-gray-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isConcierge ? 'Finding options…' : 'Thinking…'}
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              <div className="border-t border-gray-200 p-3 bg-white rounded-b-2xl shrink-0">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    onSend(inputValue);
                  }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    placeholder={
                      chatEnabled
                        ? isConcierge
                          ? 'Describe your event or service need…'
                          : 'Find vendors, ask about bookings or payments…'
                        : 'Chat unavailable'
                    }
                    disabled={isSending || !chatEnabled}
                    maxLength={2000}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm disabled:bg-gray-100"
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isSending || !chatEnabled}
                    className={`text-white px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center shrink-0 ${
                      isConcierge ? 'bg-violet-900 hover:bg-violet-800' : 'bg-black hover:bg-gray-800'
                    }`}
                    aria-label="Send"
                  >
                    {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>
                <p className="text-[10px] text-gray-500 mt-2 text-center leading-snug">
                  {isConcierge
                    ? 'AI suggestions use live listings. Confirm details on the booking page.'
                    : 'Vendor suggestions use live listings. Verify booking and payment details on each page.'}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidgetShell;
